import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Checkbox, FormControlLabel, TableCell, TableRow } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CreateIcon from "@mui/icons-material/Create";
import CloseIcon from "@mui/icons-material/Close";
import FileCopy from "@mui/icons-material/FileCopy";

// COMPONENTS
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogCheckBoxControlled from "@/main/components/shared/checkbox/checkBoxControlled";

// SERVICES
import { CityFilterRequest, MunicipalityFilterRequest, StateFilterRequest } from "@/main/models/request";
import { useDispatch } from "react-redux";
import { addressApi, cityApi, municipalityApi, stateApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { getTranslateByKey } from "@/main/services/utils";

// REDUX
import { getAddressType, useAddressTypeReducer } from "@/main/store/addressType";

import * as SP from "../styles";
import * as S from "./styles";

export interface IAddressInfoState {
	id: string;
	addressType: KeyValue;
	address: string;
	state: KeyValue;
	city: KeyValue;
	complement: string;
	number: string | null;
	zipCode: string;
	isDeliveryAddress?: boolean;
	municipality: KeyValue;
}

interface IAddressInfoProps {
	gridId?: string;
	setDataAddress: (cb: (data: IAddressInfoState[]) => IAddressInfoState[]) => void;
	dataAddress: IAddressInfoState[];
	typeCompany?: string;
	watcherCompany?: KeyValue | null;
	watcherCountry?: KeyValue | null;
	disableDeliveryAddress?: boolean;
}

const INITIAL_FORM_STATE = {
	id: "",
	addressType: null as KeyValue | null,
	address: "",
	city: null as KeyValue | null,
	state: null as KeyValue | null,
	complement: "",
	number: "",
	zipCode: "",
	isDeliveryAddress: false,
	noNumber: false,
	municipality: null as KeyValue | null,
};

export type IAddressInfoForm = typeof INITIAL_FORM_STATE;

function AddressInfo({
	gridId = "id-general-boxgrid",
	dataAddress,
	setDataAddress,
	typeCompany = "",
	watcherCompany = null,
	watcherCountry,
	disableDeliveryAddress = false,
}: IAddressInfoProps) {
	// Hooks
	const dispatch = useDispatch();
	const {
		control,
		handleSubmit,
		formState: { errors },
		clearErrors,
		setError,
		setValue,
		getValues,
		reset,
		watch: watchForm,
	} = useForm<IAddressInfoForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
	});
	const watcherNoNumber = watchForm("noNumber");
	const watcherMunicipality = watchForm("municipality");
	const watcherCity = watchForm("city");

	// Reducers
	const addressTypeReducer = useAddressTypeReducer();

	// States
	const [hasEdit, setHasEdit] = useState(false);
	const [optionsMunicipality, setOptionsMunicipality] = useState<KeyValue[] | null>(null);
	const [loadingMunicipality, setLoadingMunicipality] = useState(false);
	const [optionsCity, setOptionsCity] = useState<KeyValue[] | null>(null);
	const [loadingCity, setLoadingCity] = useState(false);

	const [optionsState, setOptionsState] = useState<KeyValue[] | null>(null);
	const [loadingState, setLoadingState] = useState(false);

	// Methods & Variables for Form
	const isDisabledMunicipalityDrop = () =>
		optionsMunicipality === null || !watcherCountry || optionsMunicipality.length === 1;
	const isDisabledStateDrop = () => optionsState === null || !watcherCountry || optionsState.length === 1;
	const isDisabledCityDrop = () => optionsCity === null || !watcherCountry || optionsCity.length === 1;

	// Handle Functions
	function handleSubmitAddressInfo(data: IAddressInfoForm) {
		setDataAddress((prevState) => {
			const haveDeliveryAddress = prevState.some((item) => item.isDeliveryAddress);

			if (haveDeliveryAddress && data.isDeliveryAddress) {
				setError("isDeliveryAddress", {
					type: "unique",
					message: getTranslateByKey("information.onlyOneDeliveryAddressIsAllowed", true),
				});
				return prevState;
			}

			const newData = {
				...data,
				number: data.noNumber ? null : data.number,
				id: uuidv4(),
			} as IAddressInfoState;

			reset();
			return [...prevState, newData];
		});
	}
	function handleEditAddressInfo(address: IAddressInfoState) {
		setHasEdit(true);
		const hasNoNumber = address.number === undefined || address.number === "" || address.number === null;
		setValue("addressType", address.addressType);
		setValue("address", address.address);
		setValue("state", address.state);
		setValue("city", address.city);
		setValue("complement", address.complement);
		setValue("noNumber", hasNoNumber);
		setValue("number", hasNoNumber ? "" : String(address.number));
		setValue("zipCode", address.zipCode);
		setValue("municipality", address.municipality);
		if (!disableDeliveryAddress && address.isDeliveryAddress !== undefined) {
			setValue("isDeliveryAddress", address.isDeliveryAddress);
		}
		setValue("id", address.id);

		loadOptionsMunicipality(watcherCountry?.key, address.zipCode);
		clearErrors();
	}
	function handleSubmitEditAddressInfo(data: IAddressInfoForm) {
		const clonePrevState = JSON.parse(JSON.stringify(dataAddress)) as IAddressInfoState[];
		const havePDeliveryAddress = clonePrevState.find((item) => item.isDeliveryAddress);
		const hasSameAddress = havePDeliveryAddress?.id === data.id;

		if (havePDeliveryAddress && data.isDeliveryAddress && !hasSameAddress) {
			setError("isDeliveryAddress", {
				type: "unique",
				message: getTranslateByKey("information.onlyOneDeliveryAddressIsAllowed", true),
			});
			return;
		}

		const findData = clonePrevState.find((item) => item.id === data.id);
		const updatedData = {
			...findData,
			...data,
			number: data.noNumber ? null : data.number,
		} as IAddressInfoState;

		setDataAddress((prevState) => prevState.map((item) => (item.id === data.id ? updatedData : item)));
		reset(INITIAL_FORM_STATE);
		setHasEdit(false);
	}

	function handleDeleteAddressInfo(id: string) {
		setHasEdit(false);
		reset();
		setDataAddress((prevState) => {
			const index = prevState.findIndex((item) => item.id === id);
			prevState.splice(index, 1);
			return [...prevState];
		});
	}

	// Async Functions
	function handleCopyAddress() {
		addressApi.getAddressBodyshop({ guidBodyShop: String(watcherCompany?.key) }).then((res) => {
			const responseAddress = res.data.result;

			if (responseAddress.length === 0) {
				toast.error(getTranslateByKey("headquarterNotHaveAddress").replace("{0}", String(watcherCompany?.value)));
				return;
			}
			const formattedAddress = responseAddress.map((address) => ({
				id: uuidv4(),
				address: address.street,
				complement: address.complement,
				number: address.number,
				zipCode: address.municipality.zipCode,
				isDeliveryAddress: String(address.delivery).toLowerCase() === "true",
				addressType: {
					key: address.addressType.idAddressType,
					value: address.addressType.name,
				},
				city: {
					key: address.municipality.city.guid,
					value: address.municipality.city.name,
				},
				municipality: {
					key: address.municipality.guid,
					value: address.municipality.name,
				},
				state: {
					key: address.municipality.city.state.guid,
					value: address.municipality.city.state.name,
				},
			})) as IAddressInfoState[];
			setDataAddress(() => formattedAddress);
		});
	}
	function callMunicipalityApiDomain(countrySelected: string | number | undefined | null, zipCode: string) {
		const MAX_LENGTH_ZIP_CODE = 5;
		setValue("state", null);
		setValue("city", null);
		setValue("municipality", null);

		setOptionsCity(null);
		setOptionsState(null);
		setOptionsMunicipality(null);

		if (countrySelected && zipCode && zipCode.length === MAX_LENGTH_ZIP_CODE) {
			const request = {
				guidCountry: String(countrySelected),
				zipCode: zipCode,
			} as MunicipalityFilterRequest;

			setLoadingMunicipality(true);
			municipalityApi
				.getDomain(request)
				.then((res) => {
					const result = res.data.result;
					if (result.length === 0) {
						toast.error(getTranslateByKey("information.zipCodeNotFound", true));
						setValue("municipality", null);
						return;
					}
					if (result.length === 1) {
						clearErrors("municipality");
						setValue("municipality", result[0]);
						return;
					}
					setOptionsMunicipality(result);
					setValue("municipality", null);
				})
				.catch(() => {})
				.finally(() => setLoadingMunicipality(false));
		}
	}
	function callStatesApiDomain(citySelected: KeyValue | null | undefined) {
		setValue("state", null);

		if (citySelected !== null && citySelected !== undefined) {
			const request = {
				guidCity: String(citySelected.key || ""),
			} as StateFilterRequest;

			setLoadingState(true);
			stateApi
				.getDomain(request)
				.then((res) => {
					const result = res.data.result;

					if (result.length === 0) {
						toast.error(getTranslateByKey("information.zipCodeNotFound"));
						setValue("state", null);
						return;
					}
					if (result.length === 1) {
						clearErrors("state");
						setValue("state", result[0]);
						return;
					}

					setOptionsState(result);
				})
				.catch(() => {})
				.finally(() => setLoadingState(false));
		}
	}
	function callCityApiDomain(municipalitySelected: KeyValue | null | undefined) {
		setValue("state", null);

		if (municipalitySelected !== null && municipalitySelected !== undefined) {
			const request = {
				guidMunicipality: String(municipalitySelected.key || ""),
			} as CityFilterRequest;

			setLoadingCity(true);
			cityApi
				.getDomain(request)
				.then((res) => {
					const result = res.data.result;
					if (result.length === 0) {
						toast.error(getTranslateByKey("information.zipCodeNotFound"));
						setValue("city", null);
						return;
					}
					if (result.length === 1) {
						clearErrors("city");
						setValue("city", result[0]);
						return;
					}

					setOptionsCity(result);
				})
				.catch(() => {})
				.finally(() => setLoadingCity(false));
		}
	}

	function loadOptionsMunicipality(countrySelected: string | number | undefined | null, zipCode: string) {
		const request = {
			guidCountry: String(countrySelected),
			zipCode: zipCode,
		} as MunicipalityFilterRequest;
		setLoadingMunicipality(true);

		municipalityApi
			.getDomain(request)
			.then((res) => {
				const result = res.data.result;
				setOptionsMunicipality(result);
			})
			.catch(() => {})
			.finally(() => setLoadingMunicipality(false));
	}

	useEffect(() => {
		dispatch(getAddressType());
	}, []);

	useEffect(() => {
		const zipCodeForm = getValues("zipCode");
		callMunicipalityApiDomain(watcherCountry?.key, zipCodeForm);
	}, [watcherCountry]);

	useEffect(() => {
		callStatesApiDomain(watcherCity);
	}, [watcherCity]);

	useEffect(() => {
		callCityApiDomain(watcherMunicipality);
	}, [watcherMunicipality]);

	// Constants Layout

	const rowHeaders = [
		getTranslateByKey("information.addressType"),
		getTranslateByKey("information.zipCode"),
		getTranslateByKey("information.state"),
		getTranslateByKey("information.city"),
		`${getTranslateByKey("information.address")} - ${getTranslateByKey("information.number")}`,
		getTranslateByKey("information.municipalityAndNeighborhood"),
		getTranslateByKey("information.complement"),
		getTranslateByKey("information.deliveryAddress"),
		getTranslateByKey("information.actions"),
	];

	if (disableDeliveryAddress) {
		const findIndex = rowHeaders.findIndex((itemRow) => itemRow === getTranslateByKey("information.deliveryAddress"));
		rowHeaders.splice(findIndex, 1);
	}

	const hasSelectedHeadquarter = watcherCompany !== null;

	return (
		<S.Container id="id-register-addressInfo">
			<form onSubmit={handleSubmit(hasEdit ? handleSubmitEditAddressInfo : handleSubmitAddressInfo)} noValidate>
				<SP.HeaderSection>
					<SP.HeaderText>{getTranslateByKey("information.addressInfo")}</SP.HeaderText>
					{typeCompany === "branch" && (
						<SecondaryButton
							type="button"
							disabled={!hasSelectedHeadquarter || dataAddress.length > 0}
							onClick={handleCopyAddress}
							style={{ padding: ".2rem .8rem", margin: "0 0 0 1rem" }}
							id="id-register-txt-copyAddress"
						>
							<FileCopy fontSize="small" />
							<span style={{ lineHeight: "0.7rem", marginLeft: ".4rem" }}>
								{getTranslateByKey("information.copyAddressesFromHeadquarters")}
							</span>
						</SecondaryButton>
					)}
				</SP.HeaderSection>
				<S.AreaForm>
					<KogInputTextControlledNew
						name="zipCode"
						label={getTranslateByKey("information.zipCode")}
						hasOnlyNumber
						control={control}
						inputProps={{ maxLength: 5 }}
						controllerProps={{ rules: { required: true } }}
						errorMessageCustom={
							errors.zipCode?.type === "required" ? getTranslateByKey("information.zipCodeRequired", true) : ""
						}
						onChangeCustom={debounce((e) => {
							callMunicipalityApiDomain(watcherCountry?.key, e);
						}, 500)}
						id="id-register-txt-zipCode"
					/>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.municipalityAndNeighborhood")}
						nameCustom="municipality"
						control={control}
						options={optionsMunicipality}
						isLoading={loadingMunicipality}
						rulesCustom={{ required: true }}
						disabled={isDisabledMunicipalityDrop()}
						onChangeCustom={() => {}}
						id="id-register-txt-municipality"
					/>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.state")}
						nameCustom="state"
						control={control}
						options={optionsState}
						isLoading={loadingState}
						rulesCustom={{ required: true }}
						hasErrorCustom={!!errors.state}
						disabled={isDisabledStateDrop()}
						errorMessageCustom={
							errors.state?.type === "required" ? getTranslateByKey("information.stateRequired", true) : ""
						}
						onChangeCustom={() => {}}
						id="id-register-txt-state"
					/>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.city")}
						nameCustom="city"
						control={control}
						options={optionsCity}
						isLoading={loadingCity}
						rulesCustom={{ required: true }}
						hasErrorCustom={!!errors.city}
						disabled={isDisabledCityDrop()}
						errorMessageCustom={
							errors.city?.type === "required" ? getTranslateByKey("information.cityRequired", true) : ""
						}
						onChangeCustom={() => {}}
						id="id-register-txt-city"
					/>
					<KogInputTextControlledNew
						name="address"
						label={getTranslateByKey("information.address")}
						control={control}
						controllerProps={{ rules: { required: true, maxLength: 200 } }}
						errorMessageCustom={
							errors.address?.type === "required" ? getTranslateByKey("information.addressRequired", true) : ""
						}
						id="id-register-txt-address"
					/>
					<S.AreaInputNumber>
						<KogInputTextControlledNew
							name="number"
							label={getTranslateByKey("information.number")}
							disabled={watcherNoNumber}
							hasOnlyNumberInteger
							control={control}
							controllerProps={{
								rules: {
									required: !watcherNoNumber,
								},
							}}
							inputProps={{ maxLength: 8 }}
							errorMessageCustom={
								errors.number?.type === "required" ? getTranslateByKey("information.numberRequired", true) : ""
							}
							id="id-register-txt-number"
						/>
						<Controller
							name="noNumber"
							control={control}
							render={({ field: { onChange, value } }) => (
								<S.AreaCheckbox>
									<FormControlLabel
										label={getTranslateByKey("information.noNumber")}
										control={
											<Checkbox
												color="primary"
												checked={value}
												onChange={(e) => {
													onChange(e.target.checked);
													if (e.target.checked) {
														clearErrors("number");
														setValue("number", "");
													}
												}}
												id="id-register-checkBox-noNumber"
											/>
										}
									/>
								</S.AreaCheckbox>
							)}
						/>
					</S.AreaInputNumber>
					<KogAutoCompleteControlled
						label={getTranslateByKey("information.addressType")}
						nameCustom="addressType"
						control={control}
						options={addressTypeReducer.addressType.data}
						isLoading={addressTypeReducer.addressType.isLoading}
						rulesCustom={{ required: true }}
						hasErrorCustom={!!errors.addressType}
						errorMessageCustom={
							errors.addressType?.type === "required" ? getTranslateByKey("information.addressTypeRequired", true) : ""
						}
						onChangeCustom={() => {}}
						id="id-register-txt-addressType"
					/>
					<KogInputTextControlledNew
						name="complement"
						label={getTranslateByKey("information.complement")}
						control={control}
						controllerProps={{ rules: { maxLength: 100 } }}
						inputProps={{ maxLength: 100 }}
						id="id-register-txt-complement"
					/>
					<div
						style={{
							height: "fit-content",
							display: "flex",
							alignItems: "flex-start",
						}}
					>
						{!disableDeliveryAddress && (
							<KogCheckBoxControlled
								nameCustom="isDeliveryAddress"
								control={control}
								label={`${getTranslateByKey("information.deliveryAddress")}?`}
								hasErrorCustom={!!errors.isDeliveryAddress}
								errorMessageCustom={errors.isDeliveryAddress?.type === "unique" ? errors.isDeliveryAddress.message : ""}
								id="id-register-checkBox-isDeliveryAddress"
							/>
						)}
						<SecondaryButton
							id="id-register-btn-addAddress"
							type="submit"
							onClick={handleSubmit(hasEdit ? handleSubmitEditAddressInfo : handleSubmitAddressInfo)}
							style={{ padding: ".2rem .8rem", margin: "0.5rem 0 0 0" }}
						>
							{hasEdit ? getTranslateByKey("information.edit") : getTranslateByKey("information.add")}
							{` ${getTranslateByKey("information.address").toLowerCase()}`}
						</SecondaryButton>
					</div>
				</S.AreaForm>
				{dataAddress.length > 0 && (
					<BoxGrid id={gridId} rowHeaders={rowHeaders}>
						{dataAddress.map((address) => (
							<TableRow key={address.id}>
								<TableCell>{address.addressType.value}</TableCell>
								<TableCell>{address.zipCode}</TableCell>
								<TableCell>{address.state.value}</TableCell>
								<TableCell>{address.city.value}</TableCell>
								<TableCell>
									{address.address} {!!address.number && ` - ${address.number}`}
								</TableCell>
								<TableCell>{address.municipality.value}</TableCell>
								<S.EllispsisTableCell>
									<S.TextEllipsis title={address.complement}>{address.complement}</S.TextEllipsis>
								</S.EllispsisTableCell>
								{!disableDeliveryAddress && (
									<TableCell>{address.isDeliveryAddress && <CheckRounded color="primary" />}</TableCell>
								)}
								<TableCell width="5%">
									<CreateIcon
										onClick={() => handleEditAddressInfo(address)}
										id={"id-register-icon-editAddress-" + address.address}
										className="cursor-pointer"
										color="primary"
									/>
									<CloseIcon
										onClick={() => handleDeleteAddressInfo(address.id)}
										id={"id-register-icon-deleteAddress-" + address.address}
										className="cursor-pointer"
										color="primary"
									/>
								</TableCell>
							</TableRow>
						))}
					</BoxGrid>
				)}
			</form>
		</S.Container>
	);
}

export default AddressInfo;
